import React, { useState } from 'react';
import { AcademicCapIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface CorporateVocabularyTerm {
  id: number;
  term: string;
  definition: string;
  context?: string;
  usage_examples?: string[];
  industry?: string;
  difficulty_level: string;
  synonyms?: string[];
  antonyms?: string[];
  company_tag?: {
    id: number;
    name: string;
    description?: string;
    color?: string;
  } | null;
  color_code?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface VocabularyTermCardProps {
  term: CorporateVocabularyTerm;
}

const VocabularyTermCard: React.FC<VocabularyTermCardProps> = ({ term }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getDifficultyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'advanced':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getIndustryColor = (industry: string) => {
    switch (industry) {
      case 'Technology':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Startups':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'Engineering':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'General Business':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Marketing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getDifficultyDisplayName = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  return (
    <>
      {/* Flashcard */}
      <div
        onClick={() => setIsModalOpen(true)}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-105 border border-gray-200 dark:border-gray-700"
        style={{ borderLeft: `4px solid ${term.color_code || '#6B7280'}` }}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{term.term}</h3>
            <AcademicCapIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4">
            {term.definition}
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {term.industry && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(term.industry)}`}>
                {term.industry}
              </span>
            )}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(term.difficulty_level)}`}>
              {getDifficultyDisplayName(term.difficulty_level)}
            </span>
          </div>

          {term.company_tag && (
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: term.company_tag.color || '#6B7280' }}
              ></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{term.company_tag.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: term.color_code || '#6B7280' }}
                ></div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{term.term}</h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Definition */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Definition</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{term.definition}</p>
              </div>

              {/* Context */}
              {term.context && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Context</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{term.context}</p>
                </div>
              )}

              {/* Usage Examples */}
              {term.usage_examples && term.usage_examples.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Usage Examples</h3>
                  <div className="space-y-3">
                    {term.usage_examples.map((example, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border-l-4 border-blue-500">
                        <p className="text-gray-700 dark:text-gray-300 italic">"{example}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Industry & Difficulty */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {term.industry && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Industry</h3>
                    <span className={`px-3 py-2 rounded-full text-sm font-medium ${getIndustryColor(term.industry)}`}>
                      {term.industry}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Difficulty Level</h3>
                  <span className={`px-3 py-2 rounded-full text-sm font-medium ${getDifficultyColor(term.difficulty_level)}`}>
                    {getDifficultyDisplayName(term.difficulty_level)}
                  </span>
                </div>
              </div>

              {/* Synonyms & Antonyms */}
              {((term.synonyms && term.synonyms.length > 0) || (term.antonyms && term.antonyms.length > 0)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {term.synonyms && term.synonyms.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Synonyms</h3>
                      <div className="flex flex-wrap gap-2">
                        {term.synonyms.map((synonym, index) => (
                          <span key={index} className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-3 py-1 rounded-full text-sm">
                            {synonym}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {term.antonyms && term.antonyms.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Antonyms</h3>
                      <div className="flex flex-wrap gap-2">
                        {term.antonyms.map((antonym, index) => (
                          <span key={index} className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 px-3 py-1 rounded-full text-sm">
                            {antonym}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Company Tag */}
              {term.company_tag && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Company Tag</h3>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: term.company_tag.color || '#6B7280' }}
                    ></div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{term.company_tag.name}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VocabularyTermCard;
